/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
    object, string, array, shape, bool,
} from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import ReactMarkdown from 'react-markdown';
import { useUIDSeed } from 'react-uid';
import withStyles from '@material-ui/core/styles/withStyles';
import GraphqlWineDisclaimer from '../GraphqlWineDisclaimer/WineDisclaimerContainer';
import { selectedSkuType } from '../common/types';

const styles = (theme) => ({
    root: {
        marginBottom: '40px',
        width: '100%',
        overflow: 'hidden',
        border: `1px solid ${theme?.palette?.grey8}`,
        minHeight: '407px',
        marginTop: '8%',
        // Mobile
        [theme.breakpoints.down(1025)]: {
            display: 'none',
        },
    },
    tabContentsList: {
        width: '31%',
        borderRight: `1px solid ${theme?.palette?.primary[200]}`,
        float: 'left',
        paddingLeft: '2%',
    },
    shortDescription: {
        padding: '0% 0% 0% 34%',
    },
    contents: {
        color: theme?.palette?.colorPrimary,
        paddingRight: '10px',
        fontWeight: '600',
        fontSize: 20,
    },
    productDetails: {
        background: theme?.palette?.globalBannerMainTextColor,
        color: theme?.palette?.common?.white,
        padding: '10px',
        fontWeight: '600',
        margin: 0,
        fontSize: 22,
    },
    tabProductDetails: {
        width: '100%',
        display: 'grid',
        paddingBottom: '2%',
        [theme.breakpoints.up('md')]: {
            display: 'block',
        },
    },
    shortDescriptionCopy: {
        width: '96%',
        paddingBottom: '10px',
    },
    additionalInformation: {
        '& img': {
            marginRight: 20,
            alignSelf: 'center',
            height: 'fit-content',
        },
        '& p': {
            display: 'flex',
            fontSize: '0.8em',
        },
        margin: '10px 0',
        padding: 10,
    },
    additionalInformationContainer: {
        width: '96%',
        borderTop: `1px solid ${theme.palette.grey8 || '#ccc'}`,
    },
    contentDesc: {
        overflowX: 'hidden',
        overflowY: 'auto',
    },
});

const GraphqlFoodDetailsUIDesktop = ({
    classes, productContentsDescription, productContents, longDescription, shortDescription, selectedSku, additionalInformation, isPassportBundleProduct,
}) => {
    const seed = useUIDSeed();
    const descriptionDiv = useRef(null);
    const [scrollState, setScrollState] = useState(200);
    useEffect(() => {
        setScrollState(descriptionDiv.current.offsetHeight - 150);
    }, [descriptionDiv]);

    return (
        <div className={classes.root} ref={descriptionDiv}>
            <div className={classes.tabProductDetails}>
                <h2 className={classes.productDetails}>Product Details</h2>
            </div>

            <div className={classes.tabContentsList}>
                <strong className={classes.contents}>Contents</strong>
                <div className={classes.contentDesc} style={{ height: `${scrollState}px` }}>
                    {productContentsDescription?.length > 0 && ReactHtmlParser(productContentsDescription)}
                    {!productContents && longDescription ? ReactHtmlParser(longDescription) : null}
                </div>
            </div>
            <div className={classes.shortDescription}>
                <strong className={classes.contents}>Description</strong>
                <div className={classes.shortDescriptionCopy}>
                    {ReactHtmlParser(shortDescription)}
                    {selectedSku?.ageVerifyFlag === 'true'
                    && (
                        <div>
                            <div className={classes.divider} />
                            <GraphqlWineDisclaimer />
                        </div>
                    )}
                </div>
                {additionalInformation.length ? (
                    <div className={classes.additionalInformationContainer}>
                        {
                            additionalInformation.map((info) => {
                                switch (Object.keys(info)?.[0]) {
                                    case 'image':
                                        return null;
                                    case 'copy':
                                        return (
                                            <div
                                                key={seed(info)}
                                                style={{ backgroundColor: info.copy?.background_color?.color }}
                                                className={classes.additionalInformation}
                                            >
                                                <ReactMarkdown source={info.copy?.copy} />
                                            </div>
                                        );
                                    default:
                                        return null;
                                }
                            })
                        }
                    </div>
                ) : null}
            </div>
            {
                isPassportBundleProduct?.enable
                &&            (
                    <div className={classes.shortDescription}>
                        <strong className={classes.contents}>Celebrations Passport Details: </strong>{isPassportBundleProduct?.details?.item_sku}
                        <div className={classes.shortDescriptionCopy}>
                            {ReactHtmlParser(isPassportBundleProduct?.details?.long_description)}
                        </div>
                    </div>
                )
            }
        </div>
    );
};

GraphqlFoodDetailsUIDesktop.propTypes = {
    classes: object.isRequired,
    shortDescription: string.isRequired,
    productContents: array.isRequired,
    longDescription: string.isRequired,
    selectedSku: selectedSkuType,
    productContentsDescription: string,
    additionalInformation: array,
    isPassportBundleProduct: shape({
        enable: bool,
        details: shape({
            long_description: string,
        }),
    }),
};

GraphqlFoodDetailsUIDesktop.defaultProps = {
    selectedSku: {},
    productContentsDescription: '',
    additionalInformation: [],
    isPassportBundleProduct: {},
};

export default withStyles(styles)(GraphqlFoodDetailsUIDesktop);
